import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Activity, CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { provisioning } from '../../services/superuserApi';

// Alvos de toque ≥ 44 px e texto ≥ 12 px: este painel é usado no telemóvel, a 390 px.
const TAP = 44;

const apiMessage = (e, fallback) => e?.response?.data?.message || e?.message || fallback;

const fmtDuration = (seconds) => {
  const total = Math.max(0, Number(seconds) || 0);
  if (total < 60) return `${total} s`;
  const minutes = Math.floor(total / 60);
  if (minutes < 60) return `${minutes} min ${total % 60} s`;
  return `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
};

const fmtDate = (value) => (value
  ? new Date(value).toLocaleString('pt-PT', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
  : '—');

const chip = (background, color) => ({
  padding: '2px 10px', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 700,
  background, color, whiteSpace: 'nowrap',
});

/**
 * Estado observável de uma instalação/actualização.
 *
 * Antes disto o ecrã dizia «A instalar» a partir de um campo escrito uma vez no início e outra no
 * fim: se o processo morresse a meio, ficava assim para sempre. Aqui mostra-se a fase, o passo, há
 * quanto tempo houve o último progresso e — passados os minutos configurados sem sinal de vida —
 * «parada», com a saída honesta: marcar como parada e repetir.
 *
 * Faz polling enquanto a operação está a decorrer e pára quando ela termina.
 */
const ProvisioningPanel = ({ instanceId, pollMs = 5000 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let timer = null;

    const tick = async () => {
      try {
        const res = await provisioning.state(instanceId);
        if (cancelled) return;
        setData(res.data);
        setLoadError(null);
        setLoading(false);
        // Só continua a perguntar enquanto houver trabalho a decorrer.
        if (res.data?.running) timer = setTimeout(tick, pollMs);
      } catch (e) {
        if (cancelled) return;
        setLoadError(apiMessage(e, 'Não foi possível ler o estado da instalação.'));
        setLoading(false);
      }
    };

    tick();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [instanceId, pollMs, reloadKey]);

  const reload = useCallback(() => setReloadKey((key) => key + 1), []);

  const abandon = async () => {
    setBusy(true);
    try {
      const res = await provisioning.abandon(instanceId);
      toast.success(res.data?.message || 'Instalação marcada como parada.');
      reload();
    } catch (e) {
      toast.error(apiMessage(e, 'Não foi possível marcar a instalação como parada.'));
      reload();
    } finally {
      setBusy(false);
    }
  };

  const phases = data?.phases || [];
  const running = !!data?.running && !data?.stalled;
  const stalled = !!data?.stalled;
  const ready = data?.status === 'Ready';
  const failed = data?.status === 'Failed';

  return (
    <div data-testid="provisioning-panel" style={styles.card}>
      <div style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
          <Activity size={18} color="var(--accent-primary)" style={{ flexShrink: 0 }} />
          <h3 style={styles.title}>Estado da instalação</h3>
        </div>
        {data?.hasRun && (
          <span data-testid="provisioning-operation" style={chip('var(--bg-tertiary)', 'var(--text-secondary)')}>
            {data.operationLabel}
            {data.releaseVersion ? ` · ${data.releaseVersion}` : ''}
          </span>
        )}
      </div>

      {loading && (
        <div style={styles.muted}><Loader2 size={16} className="spin" /> A ler o estado…</div>
      )}

      {!loading && loadError && (
        <div style={styles.errorBox} data-testid="provisioning-error">
          <div>{loadError}</div>
          <button type="button" onClick={reload} style={{ ...styles.secondary, marginTop: '0.5rem' }}>
            Tentar de novo
          </button>
        </div>
      )}

      {!loading && !loadError && data && !data.hasRun && (
        <div style={styles.muted} data-testid="provisioning-empty">
          Ainda não há nenhuma instalação registada nesta instância. A partir da próxima (Instalar
          ou Actualizar) o progresso fica visível aqui, passo a passo.
        </div>
      )}

      {!loading && !loadError && data?.hasRun && (
        <>
          <div data-testid="provisioning-phase" style={styles.phaseLine}>
            {running && <Loader2 size={16} className="spin" color="var(--accent-primary)" />}
            {ready && <CheckCircle2 size={16} color="var(--success-text, #146c2e)" />}
            {(failed || stalled) && <XCircle size={16} color="var(--error-text, #a11616)" />}
            <strong>{data.stalled ? 'Parada' : data.statusLabel}</strong>
            {!ready && !failed && !stalled && (
              <span style={styles.mutedSmall}>
                · {data.phaseLabel} · {data.step || 'a começar'} · há {fmtDuration(data.elapsedSeconds)}
              </span>
            )}
            {ready && (
              <span style={styles.mutedSmall}>
                · {data.message || 'concluída'} · {fmtDate(data.completedAt)}
              </span>
            )}
          </div>

          {stalled && (
            <div style={styles.warningBox} data-testid="provisioning-stalled">
              <div>
                Sem progresso há {fmtDuration(data.silentSeconds)} (limite: {data.stallMinutes} min).
                Parou em «{data.step || 'passo desconhecido'}». A base de dados não foi tocada —
                pode marcar como parada e repetir a operação.
              </div>
              <button
                type="button"
                data-testid="provisioning-abandon"
                onClick={abandon}
                disabled={busy}
                style={{ ...styles.secondary, marginTop: '0.6rem', opacity: busy ? 0.6 : 1 }}
              >
                {busy ? 'A marcar…' : 'Marcar como parada'}
              </button>
            </div>
          )}

          {failed && !stalled && (
            <div style={styles.errorBox} data-testid="provisioning-failed">
              <div><strong>Falhou</strong> em «{data.failedStep || data.step || '?'}».</div>
              <div style={{ marginTop: '2px' }}>{data.message || 'Sem motivo registado.'}</div>
              <div style={{ ...styles.mutedSmall, marginTop: '4px' }}>
                A base de dados não é apagada por uma instalação falhada. Corrija o que estiver
                indicado e repita.
              </div>
            </div>
          )}

          <ol data-testid="provisioning-phases" style={styles.phases}>
            {phases.map((phase) => (
              <li key={phase.key} data-testid={`provisioning-phase-${phase.key}`} style={styles.phaseItem}>
                <span style={{
                  ...styles.dot,
                  background: phase.state === 'done'
                    ? 'var(--success-text, #146c2e)'
                    : phase.state === 'failed'
                      ? 'var(--error-text, #a11616)'
                      : phase.state === 'current' ? 'var(--accent-primary)' : 'var(--border, #ddd)',
                }} />
                <span style={{
                  fontSize: '0.78rem',
                  fontWeight: phase.state === 'current' ? 700 : 500,
                  color: phase.state === 'pending' ? 'var(--text-muted, #6b6b6b)' : 'var(--text-primary, #1c1c1c)',
                }}>
                  {phase.label}
                </span>
              </li>
            ))}
          </ol>

          {running && (
            <div style={styles.mutedSmall} data-testid="provisioning-polling">
              A acompanhar sozinho… última actualização {fmtDate(data.updatedAt)}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const styles = {
  card: {
    background: 'var(--bg-secondary, #fff)',
    border: '1px solid var(--border, #e3e3e3)',
    borderRadius: '16px',
    padding: '1rem',
    marginTop: '1rem',
    color: 'var(--text-primary, #1c1c1c)',
  },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' },
  title: { margin: 0, fontSize: '1rem', fontWeight: 700 },
  phaseLine: {
    marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.4rem',
    flexWrap: 'wrap', fontSize: '0.875rem', lineHeight: 1.6,
  },
  muted: {
    marginTop: '0.75rem', fontSize: '0.85rem', color: 'var(--text-muted, #6b6b6b)',
    display: 'flex', alignItems: 'center', gap: '0.4rem', lineHeight: 1.6,
  },
  mutedSmall: { fontSize: '0.78rem', color: 'var(--text-muted, #6b6b6b)' },
  warningBox: {
    marginTop: '0.75rem', padding: '0.75rem', borderRadius: '10px', fontSize: '0.85rem',
    background: 'var(--warning-bg, #fff7e6)', color: 'var(--warning-text, #8a5a00)', lineHeight: 1.55,
  },
  errorBox: {
    marginTop: '0.75rem', padding: '0.75rem', borderRadius: '10px', fontSize: '0.85rem',
    background: 'var(--error-bg, #fdeaea)', color: 'var(--error-text, #a11616)', lineHeight: 1.5,
  },
  secondary: {
    minHeight: TAP, minWidth: TAP, padding: '0 1rem', borderRadius: '10px',
    border: '1px solid var(--border, #e3e3e3)', background: 'transparent',
    color: 'var(--text-primary, #1c1c1c)', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer',
  },
  phases: {
    margin: '0.75rem 0 0', padding: 0, listStyle: 'none',
    display: 'flex', flexWrap: 'wrap', gap: '0.35rem 0.9rem',
  },
  phaseItem: { display: 'inline-flex', alignItems: 'center', gap: '0.35rem', minHeight: '24px' },
  dot: { width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0 },
};

export { fmtDuration };
export default ProvisioningPanel;
